/**
 * Hub central de callback para el login OAuth (Google/Microsoft).
 *
 * Problema que resuelve: el proveedor (Google/Azure) exige que el `redirect_uri`
 * esté autorizado en su consola. Si usáramos el origin de cada tenant
 * (trdemo.app.tr.inprovider.com, pruebatr.app.tr..., etc.) habría que autorizar
 * cada subdominio nuevo a mano. En cambio, redirigimos SIEMPRE a un hub fijo por
 * TLD/plataforma, que se autoriza una sola vez:
 *
 *   - *.tr.inprovider.com  -> https://app.tr.inprovider.com   (todo TR, un hub general)
 *   - *.inprovider.cl      -> https://app.inprovider.cl
 *   - resto (*.inprovider.com y otros) -> https://app.inprovider.com
 *   - localhost / 127.0.0.1 -> el propio origin (desarrollo)
 *
 * El client_id sigue siendo global; lo único que cambia es el host del redirect.
 * El tenant NO se pierde: el backend resuelve el tenant por el usuario; y para el
 * popup, el origin del tenant que inició el login viaja en el `state` para poder
 * devolverle el resultado por postMessage.
 */

const TR_SUFFIX = '.tr.inprovider.com';
const CL_SUFFIX = '.inprovider.cl';

/** Devuelve el origin del hub central según el hostname del tenant que llama. */
export function resolveOAuthHubOrigin(hostname: string, protocol: string = 'https:'): string {
	const host = (hostname || '').toLowerCase();

	// Desarrollo: no hay hub central, se queda en el propio origin.
	if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
		return `${protocol}//${hostname}`;
	}

	if (host === 'app.tr.inprovider.com' || host.endsWith(TR_SUFFIX)) {
		return 'https://app.tr.inprovider.com';
	}
	if (host === 'inprovider.cl' || host.endsWith(CL_SUFFIX)) {
		return 'https://app.inprovider.cl';
	}
	// Por defecto, plataforma .com
	return 'https://app.inprovider.com';
}

/**
 * URL completa del callback de un proveedor en el hub correcto.
 * @param path ruta del callback, ej. 'google-callback' o 'microsoft-callback'
 */
export function resolveOAuthRedirectUri(path: string, loc: Location = window.location): string {
	const hub = resolveOAuthHubOrigin(loc.hostname, loc.protocol);
	return `${hub}/${path}`;
}

/**
 * Origin del hub derivado de un redirect_uri (para validar postMessage entrante
 * en el opener: el mensaje llega desde el hub, no desde el propio origin del tenant).
 */
export function hubOriginFromRedirect(redirectUri: string): string {
	try {
		return new URL(redirectUri).origin;
	} catch {
		return '';
	}
}
